import React, { useEffect, useRef, useState } from "react";
import SignaturePad from "signature_pad";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { PenLine, Upload, Trash2, Eraser, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { profileAPI } from "../../services/api";

/**
 * Signature Modal — three flows:
 *   1. Draw a signature on a touch/mouse signature pad
 *   2. Upload a signature image (PNG with alpha works best)
 *   3. Remove the saved signature
 *
 * The signature is normalized server-side to PNG with alpha and a
 * sane max width. The cover-letter renderer scales it proportionally
 * into the signature slot so it never bleeds into nearby content.
 */
export default function SignatureModal({ open, onOpenChange, hasSignature, onChange }) {
  const [mode, setMode] = useState("choose"); // choose | draw | preview
  const [previewUrl, setPreviewUrl] = useState("");
  const [pendingBlob, setPendingBlob] = useState(null);
  const [busy, setBusy] = useState(false);

  const canvasRef = useRef(null);
  const padRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setMode("choose");
      setPreviewUrl("");
      setPendingBlob(null);
      setBusy(false);
      if (padRef.current) padRef.current.off();
      padRef.current = null;
    }
  }, [open]);

  // (Re-)initialize the signature pad whenever we enter "draw" mode
  // and the canvas is mounted. Setting backgroundColor to transparent
  // keeps the saved PNG clean on top of the cover-letter template.
  useEffect(() => {
    if (mode !== "draw" || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    canvas.width = canvas.offsetWidth * ratio;
    canvas.height = canvas.offsetHeight * ratio;
    canvas.getContext("2d").scale(ratio, ratio);
    const pad = new SignaturePad(canvas, {
      backgroundColor: "rgba(0,0,0,0)",
      penColor: "#0b1f4a",
      minWidth: 0.8,
      maxWidth: 2.2,
    });
    padRef.current = pad;
    return () => {
      pad.off();
      padRef.current = null;
    };
  }, [mode]);

  const handleClearDraw = () => {
    padRef.current?.clear();
  };

  const handleAcceptDraw = () => {
    const pad = padRef.current;
    if (!pad || pad.isEmpty()) {
      toast.error("Draw your signature first.");
      return;
    }
    // toDataURL returns a *trimmed PNG only if* the pad is in image mode;
    // we capture the raw canvas and let the backend normalize it.
    const dataUrl = pad.toDataURL("image/png");
    fetch(dataUrl)
      .then((r) => r.blob())
      .then((blob) => {
        setPendingBlob(blob);
        setPreviewUrl(URL.createObjectURL(blob));
        setMode("preview");
      })
      .catch(() => toast.error("Couldn't capture signature."));
  };

  const onFilePicked = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/^image\/(jpe?g|png|webp)$/i.test(file.type)) {
      toast.error("Please choose a JPG, PNG, or WebP image.");
      return;
    }
    if (file.size > 3 * 1024 * 1024) {
      toast.error("Signature image must be smaller than 3MB.");
      return;
    }
    setPendingBlob(file);
    setPreviewUrl(URL.createObjectURL(file));
    setMode("preview");
  };

  const handleSave = async () => {
    if (!pendingBlob) return;
    try {
      setBusy(true);
      const file = pendingBlob instanceof File
        ? pendingBlob
        : new File([pendingBlob], `signature-${Date.now()}.png`, { type: "image/png" });
      const res = await profileAPI.uploadSignature(file);
      toast.success("Signature saved. It'll appear on your cover letter.");
      onChange?.(res.signature_url);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Failed to save signature.");
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async () => {
    try {
      setBusy(true);
      await profileAPI.deleteSignature();
      toast.success("Signature removed.");
      onChange?.("");
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.message || "Failed to remove signature.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="signature-modal">
        <DialogHeader>
          <DialogTitle>Your signature</DialogTitle>
          <DialogDescription>
            Draw a signature or upload a transparent PNG. It'll be placed automatically on your cover letter.
          </DialogDescription>
        </DialogHeader>

        {mode === "choose" && (
          <div className="space-y-3">
            <Button
              variant="outline"
              className="w-full justify-start"
              onClick={() => setMode("draw")}
              data-testid="signature-modal-draw-btn"
            >
              <PenLine className="h-4 w-4 mr-2" /> Draw signature
            </Button>
            <Button
              variant="outline"
              className="w-full justify-start"
              onClick={() => fileInputRef.current?.click()}
              data-testid="signature-modal-upload-btn"
            >
              <Upload className="h-4 w-4 mr-2" /> Upload signature image
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={onFilePicked}
              className="hidden"
            />
            {hasSignature && (
              <Button
                variant="ghost"
                className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50"
                onClick={handleRemove}
                disabled={busy}
                data-testid="signature-modal-remove-btn"
              >
                {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
                Remove current signature
              </Button>
            )}
          </div>
        )}

        {mode === "draw" && (
          <div className="space-y-3">
            <div className="border-2 border-dashed border-slate-300 rounded-lg bg-white">
              <canvas
                ref={canvasRef}
                className="w-full h-48 touch-none rounded-lg"
                data-testid="signature-canvas"
              />
            </div>
            <p className="text-xs text-slate-500">
              Tip: sign large — the system will scale it proportionally on your cover letter.
            </p>
            <div className="flex gap-2">
              <Button
                onClick={handleAcceptDraw}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
                data-testid="signature-modal-accept-draw-btn"
              >
                Use this signature
              </Button>
              <Button variant="outline" onClick={handleClearDraw}>
                <Eraser className="h-4 w-4 mr-2" /> Clear
              </Button>
              <Button
                variant="ghost"
                onClick={() => setMode("choose")}
              >
                Back
              </Button>
            </div>
          </div>
        )}

        {mode === "preview" && previewUrl && (
          <div className="space-y-3">
            <div className="border rounded-lg bg-slate-50 p-4 flex items-center justify-center min-h-[120px]">
              <img
                src={previewUrl}
                alt="Signature preview"
                className="max-h-32 max-w-full"
                data-testid="signature-modal-preview-img"
              />
            </div>
            <div className="flex gap-2">
              <Button
                onClick={handleSave}
                disabled={busy}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
                data-testid="signature-modal-save-btn"
              >
                {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                Save signature
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setPreviewUrl("");
                  setPendingBlob(null);
                  setMode("choose");
                }}
                disabled={busy}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        <DialogFooter />
      </DialogContent>
    </Dialog>
  );
}
